import React, { useEffect, useState } from 'react';
import { 
  AlertCircle, Key, TrendingUp, Zap, ThumbsUp
} from 'lucide-react';
import { getSummary14d } from '../lib/api';

// Interfaces originales
interface Term {
  term: string;
  count: number;
  impact: string;
  change_pct: number;
}

interface SentimentTrend {
  positive_change: number;
  neutral_change: number;
  negative_change: number;
}

interface SentimentDistributionOriginal {
  positive: number;
  neutral: number;
  negative: number;
}

interface Props {
  mostUsed?: Term[];
  positiveTerms?: Term[];
  improvementTerms?: Term[];
  sentiment?: SentimentDistributionOriginal;
  trend?: SentimentTrend;
  forceReload?: boolean;
}

/**
 * VerbalAnalysis - Versión completa con visualización de sentimiento y análisis de palabras clave
 */
export const VerbalAnalysis: React.FC<Props> = ({
  forceReload = true
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<any>(null);

  // Cargar datos directamente desde la API, como en el Dashboard
  useEffect(() => {
    setLoading(true);
    getSummary14d()
      .then(data => {
        setSummary(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error al cargar datos de análisis verbal:', err);
        setError('No se pudieron cargar los datos de análisis. Intente de nuevo más tarde.');
        setLoading(false);
      });
  }, [forceReload]);

  // Replicando la lógica del Dashboard
  const actual = summary?.semana_actual;
  const comparacion = summary?.comparacion;

  // Mostrar estado de carga
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-indigo-300">
        <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mb-4"></div>
        <p>Cargando análisis verbal...</p>
      </div>
    );
  }

  // Mostrar error si ocurre
  if (error) {
    return (
      <div className="flex items-center justify-center p-6 bg-red-900/20 backdrop-blur-sm rounded-xl border border-red-700/30">
        <AlertCircle className="text-red-400 mr-2" size={20} />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  // Verificar si hay datos
  if (!actual || !actual.sentimientos || !actual.top_keywords) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-indigo-900/30 backdrop-blur-sm rounded-xl border border-yellow-600/20 text-center">
        <AlertCircle className="text-yellow-400 mb-3" size={24} />
        <p className="text-yellow-400 text-lg">No hay datos de sentimiento disponibles.</p>
        <p className="text-indigo-300 mt-2 text-sm">Intente seleccionar otra fecha o verifique que existan conversaciones.</p>
      </div>
    );
  }

  const total_sentimientos= (actual.sentimientos.positivo ?? 0) + (actual.sentimientos.neutro ?? 0) + (actual.sentimientos.negativo ?? 0)
  return (
    <div className="space-y-6">
      {/* Componente de Distribución de Sentimiento - replicado exactamente del Dashboard */}
      <div className="bg-gradient-to-r from-purple-950 via-purple-800 to-purple-600 backdrop-blur-sm border border-indigo-300/20 rounded-xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl">
        <h2 className="text-xl font-semibold text-white mb-6">Distribución de Sentimiento</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-green-900/40 to-green-700/20 rounded-xl p-5 border border-green-600/20 transition-all duration-300 hover:border-green-500/40">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-6xl font-bold text-white mb-1">{Math.round((comparacion.distribucion_sentimientos.positivo/comparacion.distribucion_sentimientos.total *100 || 0))}%</h3>
                <p className="text-xl text-green-300 mb-1">Positivo</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-green-800/30 flex items-center justify-center border border-green-500/30">
                <div className="w-12 h-12 rounded-full bg-green-500/30 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-green-400/50"></div>
                </div>
              </div>
            </div>
            <p className="text-green-400 text-sm mt-3 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>
                {comparacion?.sentimientos?.positivo?.cambio_pct
                  ? `${comparacion.sentimientos.positivo.cambio_pct.toFixed(0)}%`
                  : '0%'} vs. semana anterior
              </span>
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-900/40 to-blue-700/20 rounded-xl p-5 border border-blue-600/20 transition-all duration-300 hover:border-blue-500/40">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-6xl font-bold text-white mb-1">{Math.round((comparacion.distribucion_sentimientos.neutral/comparacion.distribucion_sentimientos.total *100 || 0))}%</h3>
                <p className="text-xl text-blue-300 mb-1">Neutro</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-blue-800/30 flex items-center justify-center border border-blue-500/30">
                <div className="w-12 h-12 rounded-full bg-blue-500/30 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-blue-400/50"></div>
                </div>
              </div>
            </div>
            <p className="text-blue-400 text-sm mt-3 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>
                {comparacion?.sentimientos?.neutral?.cambio_pct
                  ? `${comparacion.sentimientos.neutral.cambio_pct.toFixed(0)}%`
                  : '0%'} vs. semana anterior
              </span>
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-red-900/40 to-red-700/20 rounded-xl p-5 border border-red-600/20 transition-all duration-300 hover:border-red-500/40">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-6xl font-bold text-white mb-1">{Math.round((comparacion.distribucion_sentimientos.negativo/comparacion.distribucion_sentimientos.total * 100 || 0))}%</h3>
                <p className="text-xl text-red-300 mb-1">Negativo</p>
              </div>
              <div className="w-16 h-16 rounded-full bg-red-800/30 flex items-center justify-center border border-red-500/30">
                <div className="w-12 h-12 rounded-full bg-red-500/30 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-red-400/50"></div>
                </div>
              </div>
            </div>
            <p className="text-red-400 text-sm mt-3 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              <span>
                {comparacion?.sentimientos?.negativo?.cambio_pct
                  ? `${comparacion.sentimientos.negativo.cambio_pct.toFixed(0)}%`
                  : '0%'} vs. semana anterior
              </span>
            </p>
          </div>
        </div>
        
        {/* Palabras Clave más frecuentes - replicado exactamente del Dashboard */}
        <div className="mt-8">
          <div className="flex items-center mb-4">
            <Key className="text-yellow-500 mr-2" size={20} />
            <h3 className="text-lg text-white font-medium">Palabras Clave más frecuentes</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {actual.top_keywords.slice(0, 3).map(([term, count], index) => (
              <div 
                key={index}
                className="bg-indigo-800/30 backdrop-blur-sm rounded-lg p-3 border border-indigo-600/20 flex justify-between items-center transition-all duration-300 hover:border-indigo-500/40"
              >
                <span className="text-indigo-100">{term}</span>
                <span className="bg-indigo-700/60 text-white px-2 py-1 rounded text-sm">
                  {count} menciones
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Análisis de Palabras Clave con barras de progreso */}
      <div className="bg-gradient-to-r from-purple-950 via-purple-800 to-purple-600 backdrop-blur-sm border border-indigo-300/20 rounded-xl p-6 shadow-lg transition-all duration-300 hover:shadow-xl">
        <h2 className="text-xl font-semibold text-white mb-2">Análisis de Palabras Clave</h2>
        <p className="text-indigo-200 mb-6 text-sm">Las palabras definen experiencias. Tu análisis mejora decisiones.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Palabras Más Utilizadas */}
          <div className="bg-gradient-to-br from-blue-900/30 to-indigo-800/10 rounded-xl p-5 border border-blue-700/20 transition-all duration-300 hover:border-blue-600/30">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center">
                <div className="p-2 bg-blue-800/40 rounded-lg mr-2">
                  <Key className="h-5 w-5 text-blue-300" />
                </div>
                <h3 className="text-white font-medium">Palabras Más Utilizadas</h3>
              </div>
              <div className="bg-blue-800/50 text-blue-200 px-2 py-1 rounded-full text-xs">
                Total: {
                  actual.top_keywords
                    .slice(0, 3)
                    .reduce((acc, [_, count]) => acc + count, 0)
                }
              </div>
            </div>
            
            {actual.top_keywords.slice(0, 3).map(([term, count], index) => (
              <div key={index} className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-100">{term}</span>
                  <span className="text-blue-300">{count}</span>
                </div>
                <div className="h-1.5 bg-blue-900/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-700"
                    style={{ width: `${(count / (actual.top_keywords[0]?.[1] || 12)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Términos de Mejora */}
          <div className="bg-gradient-to-br from-red-900/30 to-red-800/10 rounded-xl p-5 border border-red-700/20 transition-all duration-300 hover:border-red-600/30">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center">
                <div className="p-2 bg-red-800/40 rounded-lg mr-2">
                  <Zap className="h-5 w-5 text-red-300" />
                </div>
                <h3 className="text-white font-medium">Términos de Mejora</h3>
              </div>
              <div className="bg-red-800/50 text-red-200 px-2 py-1 rounded-full text-xs">
                Total: {
                  actual.terminos_mejora
                    .slice(0, 3)
                    .reduce((acc, [_, count]) => acc + count, 0)
                }  
              </div>
            </div>
            
            {actual.terminos_mejora.slice(0, 3).map(([term, count], index) => (
              <div key={index} className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-red-100">{term}</span>
                  <span className="text-red-300">{count}</span>
                </div>
                <div className="h-1.5 bg-red-900/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full transition-all duration-700"
                    style={{ width: `${(count / (actual.terminos_mejora[0]?.[1] || 2)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Términos Positivos */}
          <div className="bg-gradient-to-br from-green-900/30 to-emerald-800/10 rounded-xl p-5 border border-green-700/20 transition-all duration-300 hover:border-green-600/30">
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center">
                <div className="p-2 bg-green-800/40 rounded-lg mr-2">
                  <ThumbsUp className="h-5 w-5 text-green-300" />
                </div>
                <h3 className="text-white font-medium">Términos Positivos</h3>
              </div>
              <div className="bg-green-800/50 text-green-200 px-2 py-1 rounded-full text-xs">
                Total: {
                  actual.terminos_positivos
                    .slice(0, 3)
                    .reduce((acc, [_, count]) => acc + count, 0)
                }
              </div>
            </div>
            
            {actual.terminos_positivos.slice(0, 3).map(([term, count], index) => (
              <div key={index} className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-green-100">{term}</span>
                  <span className="text-green-300">{count}</span>
                </div>
                <div className="h-1.5 bg-green-900/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-600 to-green-400 rounded-full transition-all duration-700"
                    style={{ width: `${(count / (actual.terminos_positivos[0]?.[1] || 5)) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerbalAnalysis;